/**
 * F106 — Página /seguridad: gestión de la propia cuenta (2FA, etc.).
 *
 * Activar 2FA: enroll → muestra QR + secret manual → user mete código TOTP →
 *   activa + muestra 8 backup codes UNA VEZ → user los copia/descarga.
 *
 * Desactivar 2FA: pide código TOTP del Authenticator O un backup code.
 */
import { useState } from "react";
import { ShieldCheck, ShieldOff, Smartphone, Copy, Download, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/lib/auth";
import { formatDateTime } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import { useDisable2FA, useEnroll2FA, useTFAStatus, useVerify2FA } from "./api";
import { getTz } from "@/lib/tz";
// v1.1.3 — ColorPersonalSection removida (sistema branding eliminado).

type EnrollStep = "idle" | "qr" | "backup_codes";

export function SeguridadPage() {
  const user = useAuthStore((s) => s.user);
  const { data: status, isLoading } = useTFAStatus();

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <div>
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <ShieldCheck size={16} className="text-primary" />
          Seguridad de mi cuenta
        </h1>
        <p className="text-xs text-muted-foreground">
          Gestiona el segundo factor de autenticación (2FA) de
          <span className="font-mono"> {user?.email}</span>.
        </p>
      </div>

      {/* Card 2FA */}
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <div className={`mt-0.5 rounded-md p-2 ${status?.enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
              <Smartphone size={18} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-sm">Autenticación de dos factores</h2>
                {isLoading ? (
                  <Loader2 size={12} className="animate-spin text-muted-foreground" />
                ) : status?.enabled ? (
                  <Badge variant="success" className="text-[10px]">Activo</Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">Inactivo</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Al iniciar sesión, además de tu password se te pedirá un código de 6 dígitos generado por
                Google Authenticator, Authy, 1Password, o cualquier app TOTP compatible.
              </p>
              {status?.enabled && status.enabled_at && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Activado el {formatDateTime(status.enabled_at)}.
                </p>
              )}
            </div>
          </div>
          <div>
            {status?.enabled ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDisableOpen(true)}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
              >
                <ShieldOff size={12} /> Desactivar
              </Button>
            ) : (
              <Button size="sm" onClick={() => setEnrollOpen(true)}>
                <ShieldCheck size={12} /> Activar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* v1.1.3 — Color personal removido (sistema branding eliminado) */}

      {/* Modales */}
      <EnrollDialog open={enrollOpen} onClose={() => setEnrollOpen(false)} />
      <DisableDialog open={disableOpen} onClose={() => setDisableOpen(false)} />
    </div>
  );
}

// ─── Modal Activar ──────────────────────────────────────────────────────
function EnrollDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState<EnrollStep>("idle");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enrollData, setEnrollData] = useState<{ qr: string; secret: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const enrollMut = useEnroll2FA();
  const verifyMut = useVerify2FA();

  // Iniciar enroll cuando se abre
  function handleStart() {
    setError(null);
    enrollMut.mutate(undefined, {
      onSuccess: (res) => {
        setEnrollData({ qr: res.qr_png_base64, secret: res.secret });
        setStep("qr");
      },
      onError: (e) => setError((e as ApiError).message),
    });
  }

  function close() {
    setStep("idle");
    setCode("");
    setError(null);
    setEnrollData(null);
    setBackupCodes([]);
    onClose();
  }

  async function handleVerify() {
    setError(null);
    try {
      const res = await verifyMut.mutateAsync(code);
      setBackupCodes(res.backup_codes);
      setStep("backup_codes");
      toast.success("2FA activado");
    } catch (e) {
      setError((e as ApiError).message);
    }
  }

  function copyAllBackup() {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Códigos copiados al portapapeles");
  }

  function downloadBackup() {
    const txt = `ByteISP — Códigos de recuperación 2FA
Generados: ${new Date().toLocaleString("es-MX", { timeZone: getTz() })}

GUARDA ESTE ARCHIVO EN UN LUGAR SEGURO.
Cada código sirve UNA SOLA VEZ si pierdes acceso a tu Authenticator.

${backupCodes.join("\n")}
`;
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `byteisp-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={16} /> Activar 2FA
          </DialogTitle>
          <DialogDescription>
            {step === "idle" && "Vamos a generar un secret nuevo y un código QR."}
            {step === "qr" && "Escanea el QR con tu Authenticator y mete el código de 6 dígitos."}
            {step === "backup_codes" && (
              <span className="text-emerald-700 dark:text-emerald-400">
                ✓ 2FA activado. Guarda estos códigos antes de cerrar.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === "idle" && (
          <div className="py-4 text-center">
            <p className="text-xs text-muted-foreground mb-3">
              Necesitarás Google Authenticator, Authy, 1Password o cualquier app
              compatible con TOTP en tu teléfono.
            </p>
            <Button onClick={handleStart} disabled={enrollMut.isPending}>
              {enrollMut.isPending ? "Generando…" : "Generar QR"}
            </Button>
            {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
          </div>
        )}

        {step === "qr" && enrollData && (
          <div className="space-y-3">
            <div className="flex items-center justify-center rounded-md bg-white p-3">
              <img
                src={`data:image/png;base64,${enrollData.qr}`}
                alt="QR de 2FA"
                width={180}
                height={180}
              />
            </div>
            <div className="rounded-md border bg-muted/30 p-2">
              <Label className="text-[10px] uppercase">Secret manual</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs break-all">{enrollData.secret}</code>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(enrollData.secret);
                    toast.success("Secret copiado");
                  }}
                >
                  <Copy size={12} />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Si tu app no escanea QRs, mete este código manual.
              </p>
            </div>
            <div>
              <Label>Código de tu Authenticator</Label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                autoFocus
                className="font-mono text-lg text-center tracking-widest"
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        )}

        {step === "backup_codes" && (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
              <strong>⚠️ Guárdalos AHORA.</strong> No los volverás a ver. Sirven UNA VEZ
              cada uno si pierdes acceso a tu Authenticator.
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 p-3">
              {backupCodes.map((c) => (
                <code key={c} className="font-mono text-sm text-center py-1 bg-background rounded">
                  {c}
                </code>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copyAllBackup}>
                <Copy size={12} /> Copiar todos
              </Button>
              <Button size="sm" variant="outline" onClick={downloadBackup}>
                <Download size={12} /> Descargar .txt
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          {step !== "backup_codes" && (
            <Button variant="ghost" size="sm" onClick={close}>
              Cancelar
            </Button>
          )}
          {step === "qr" && (
            <Button
              size="sm"
              onClick={handleVerify}
              disabled={code.length !== 6 || verifyMut.isPending}
            >
              <CheckCircle2 size={12} />
              {verifyMut.isPending ? "Verificando…" : "Activar 2FA"}
            </Button>
          )}
          {step === "backup_codes" && (
            <Button size="sm" onClick={close}>
              <CheckCircle2 size={12} /> Listo, ya los guardé
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal Desactivar ───────────────────────────────────────────────────
function DisableDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const disableMut = useDisable2FA();

  function close() {
    setCode("");
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    setError(null);
    try {
      await disableMut.mutateAsync(code.trim());
      toast.success("2FA desactivado");
      close();
    } catch (e) {
      setError((e as ApiError).message);
    }
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <ShieldOff size={16} /> Desactivar 2FA
          </DialogTitle>
          <DialogDescription>
            Para confirmar, mete un código actual de tu Authenticator (6 dígitos)
            o un backup code (formato XXXX-XXXX). Es el mismo doble factor que usas
            para entrar.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Label>Código TOTP o backup code</Label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456 o XXXX-XXXX"
            autoFocus
            className="font-mono text-center"
          />
          {error && (
            <div className="mt-2 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
            Tu cuenta volverá a usar solo password. Si quieres reactivarlo después,
            tendrás que escanear un nuevo QR.
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={close}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={!code || disableMut.isPending}
            onClick={handleSubmit}
          >
            {disableMut.isPending ? "Desactivando…" : "Desactivar 2FA"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
